import { PortableText, PortableTextComponents } from "@portabletext/react";
import * as Sentry from "@sentry/nextjs";
import { BlockContent, Break, CustomImage, Video } from "../types/schema";
import { getClassName } from "@pairtreefamily/utils";
import { getImageURL } from "../utils/getImageURL";
import BreakRenderer from "./break";
import { ListItem, OrderedList, UnorderedList } from "./list";
import { VimeoButton, YouTubeButton } from "./videoButton";
import { VimeoEmbed, YouTubeEmbed } from "./videoEmbed";

export type RichTextProps = {
  colorway?: string;
  content: BlockContent | undefined;
};

export type ColorEditor = {
  colorName: "darkblue" | "teal" | "green" | "lightgreen" | "peach" | "rust";
};

export function RichText(props: RichTextProps) {
  if (!props.content) {
    return null;
  }

  return (
    <div className={`${props.colorway}-colorway`}>
      <PortableText value={props.content} components={portableTextComponents} />
    </div>
  );
}

// helper type for typing the override components' prop objects
type Value<T> = {
  value: T;
};

const imageClass = getClassName(
  "mr-auto my-16",
  "rounded-2xl",
  "max-h-[370px] object-cover",
);

export const colorMap = {
  darkblue: "text-darkblue",
  teal: "text-teal",
  green: "text-green",
  lightgreen: "text-lightgreen",
  peach: "text-peach",
  rust: "text-rust",
};

// keys in the `types` object need to match document _type
const portableTextComponents: PortableTextComponents = {
  // keys in the `types` object correspond to _type attribute in schema
  // this handles all non-'block' types defined in blockContent
  types: {
    image: ({ value }) => {
      if (!value?.asset?._ref) {
        return null;
      }
      return (
        <img
          className={imageClass}
          alt={value.alt || ""}
          loading="lazy"
          src={getImageURL(value).fit("max").url()}
        />
      );
    },
    customImage: ({ value }: Value<CustomImage>) => {
      if (!value?.asset?._ref) {
        return null;
      }
      return (
        <img
          alt={value.alt}
          className={imageClass}
          loading="lazy"
          src={getImageURL(value).fit("max").url()}
        />
      );
    },
    video: ({ value }: Value<Video>) => {
      switch (value.platform) {
        case undefined:
          return null;
        case "Vimeo":
          if (!value.vimeo?.videoId) {
            return null;
          }
          if (value.vimeo.text) {
            return (
              <VimeoButton
                className="mx-auto my-4 block"
                videoId={value.vimeo.videoId}
                videoHash={value.vimeo.videoHash}
                label={value.vimeo.text || "Watch Video"}
              />
            );
          } else {
            return (
              <div className="my-8">
                <VimeoEmbed
                  videoId={value.vimeo.videoId}
                  videoHash={value.vimeo.videoHash}
                />
              </div>
            );
          }
        case "YouTube":
          if (!value.youtube?.videoId) {
            return null;
          }
          if (value.youtube.text) {
            return (
              <YouTubeButton
                className="mx-auto my-4 block"
                videoId={value.youtube.videoId}
                label={value.youtube.text || "Watch Video"}
              />
            );
          } else {
            return (
              <div className="my-8">
                <YouTubeEmbed videoId={value.youtube.videoId} />
              </div>
            );
          }
        default:
          const canary: never = value.platform;
          Sentry.captureMessage(
            `Received unknown value for video.platform: ${canary}`,
          );

          return null;
      }
    },
    break: ({ value }: Value<Break>) => {
      if (value.type === "section") {
        return <BreakRenderer />;
      }
      return null;
    },
  },
  // This corresponds to the 'list' field of the 'block' schema
  list: {
    bullet: ({ children }) => (
      <UnorderedList style="disc">{children}</UnorderedList>
    ),
    number: ({ children, value }) => (
      <OrderedList style={value.level === 1 ? "decimal" : "alpha"}>
        {children}
      </OrderedList>
    ),
    check: ({ children }) => (
      <UnorderedList style="check">{children}</UnorderedList>
    ),
  },
  listItem: {
    bullet: ({ children }) => <ListItem>{children}</ListItem>,
    number: ({ children }) => <ListItem>{children}</ListItem>,
    check: ({ children }) => <ListItem>{children}</ListItem>,
  },
  // This corresponds to the 'marks' field of the 'block' schema. Both
  // annotations and decorators are handled here
  marks: {
    link: ({ children, value }) => (
      <a href={value.href}>
        <span className="text-teal">{children}</span>
      </a>
    ),
    color: ({ value, children }) => {
      if (value.colorName in colorMap) {
        return (
          <span
            className={getClassName(
              colorMap[(value as ColorEditor)?.colorName],
            )}
          >
            {children}
          </span>
        );
      }
      return <span style={{ color: value.colorName }}>{children}</span>;
    },
  },

  // 'main' block content overrides. This corresponds to the 'styles' field of
  // the 'block' schema
  block: {
    h1: ({ children }) => <h2 className="text-head-8 my-8">{children}</h2>,
    h2: ({ children }) => <h3 className="text-head-6 my-6">{children}</h3>,
    h3: ({ children }) => <h4 className="text-head-7 my-4">{children}</h4>,
    h4: ({ children }) => <h5 className="text-overline-3 my-3">{children}</h5>,
    normal: ({ children }) => (
      <p className="text-body-2-medium my-4 [&>strong]:text-black">
        {children}
      </p>
    ),
    small: ({ children }) => (
      <p className="text-body-3-medium my-4 [&>strong]:text-black">
        {children}
      </p>
    ),
    xsmall: ({ children }) => (
      <p className="text-body-4 my-4 [&>strong]:text-black">{children}</p>
    ),
    large: ({ children }) => (
      <p className="text-body-1 my-4 [&>strong]:text-black">{children}</p>
    ),
    blockquote: ({ children }) => (
      <figure className="text-quotation-3 my-4 text-center text-green md:my-8">
        {children}
      </figure>
    ),
    overline: ({ children }) => <p className="text-overline-2">{children}</p>,
  },
};
